import React, { useEffect, useState } from 'react';
import {
  FixedAsset,
  FixedAssetSearchFilter,
} from './fixedAssetTypes';

interface FixedAssetListProps {
  assets: FixedAsset[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  search: FixedAssetSearchFilter;
  onSearchChange: (value: FixedAssetSearchFilter) => void;
  onPageChange: (page: number) => void;
  onReload: () => void | Promise<void>;
  onSelect: (asset: FixedAsset | null) => void;
  selectedAssetId: number | null;
  onEdit: (asset: FixedAsset) => void;
  onDelete: (asset: FixedAsset) => void;
  onCalculate: (asset: FixedAsset) => void;
  apiFetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

const FixedAssetList: React.FC<FixedAssetListProps> = ({
  assets,
  total,
  page,
  pageSize,
  loading,
  search,
  onSearchChange,
  onPageChange,
  onReload,
  onSelect,
  selectedAssetId,
  onEdit,
  onDelete,
  onCalculate,
  apiFetch,
}) => {
  const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);
  const [downloadingExcel, setDownloadingExcel] = useState<boolean>(false);
  const [downloadingInventory, setDownloadingInventory] = useState<boolean>(false);
  const [localSearch, setLocalSearch] = useState<FixedAssetSearchFilter>(search);

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  const handleChange = (field: keyof FixedAssetSearchFilter, value: string) => {
    const next = { ...localSearch, [field]: value };
    setLocalSearch(next);
    onSearchChange(next);
  };

  const maxPage = Math.max(1, Math.ceil(total / pageSize));

  const handlePdfDownload = async () => {
    if (!apiFetch) return;
    setDownloadingPdf(true);
    try {
      const response = await apiFetch('/api/fixed-assets/pdf');
      if (!response.ok) {
        throw new Error('PDF出力に失敗しました');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `固定資産台帳_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('PDF出力に失敗しました');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleExcelDownload = async () => {
    if (!apiFetch) return;
    setDownloadingExcel(true);
    try {
      const response = await apiFetch('/api/fixed-assets/excel');
      if (!response.ok) {
        throw new Error('Excel出力に失敗しました');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `固定資産台帳_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Excel download error:', error);
      alert('Excel出力に失敗しました');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleInventoryDownload = async () => {
    if (!apiFetch) return;
    setDownloadingInventory(true);
    try {
      const response = await apiFetch('/api/fixed-assets/property-inventory/excel');
      if (!response.ok) {
        throw new Error('財産目録Excel出力に失敗しました');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `財産目録_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Inventory download error:', error);
      alert('財産目録Excel出力に失敗しました');
    } finally {
      setDownloadingInventory(false);
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('ja-JP');
  };

  const formatDate = (value: string) => {
    if (!value) {
      return '';
    }
    try {
      return new Date(value).toISOString().slice(0, 10);
    } catch {
      return value;
    }
  };

  return (
    <div
      style={{
        border: '2px inset #d0d0d0',
        padding: '12px',
        backgroundColor: '#ffffff',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: '420px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#003366',
          }}
        >
          固定資産一覧
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={onReload}
            style={{
              backgroundColor: '#f0f0f0',
              border: '2px outset #e0e0e0',
              padding: '3px 8px',
              fontSize: '11px',
              cursor: 'pointer',
            }}
            disabled={loading}
          >
            再読み込み
          </button>
          {apiFetch && (
            <>
              <button
                onClick={handlePdfDownload}
                style={{
                  backgroundColor: '#fff4e0',
                  border: '2px outset #ffcc80',
                  padding: '3px 8px',
                  fontSize: '11px',
                  cursor: downloadingPdf ? 'wait' : 'pointer',
                }}
                disabled={downloadingPdf || loading}
              >
                {downloadingPdf ? 'PDF出力中...' : '台帳PDF'}
              </button>
              <button
                onClick={handleExcelDownload}
                style={{
                  backgroundColor: '#e0f2f1',
                  border: '2px outset #80cbc4',
                  padding: '3px 8px',
                  fontSize: '11px',
                  cursor: downloadingExcel ? 'wait' : 'pointer',
                }}
                disabled={downloadingExcel || loading}
              >
                {downloadingExcel ? 'Excel出力中...' : '台帳Excel'}
              </button>
              <button
                onClick={handleInventoryDownload}
                style={{
                  backgroundColor: '#e8eaf6',
                  border: '2px outset #9fa8da',
                  padding: '3px 8px',
                  fontSize: '11px',
                  cursor: downloadingInventory ? 'wait' : 'pointer',
                }}
                disabled={downloadingInventory || loading}
              >
                {downloadingInventory ? '目録出力中...' : '財産目録Excel'}
              </button>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '8px',
          fontSize: '11px',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column' }}>
          資産名称で検索
          <input
            type="text"
            value={localSearch.assetName}
            onChange={(e) => handleChange('assetName', e.target.value)}
            style={{ padding: '4px', border: '1px inset #d0d0d0', fontSize: '11px' }}
            placeholder="キーワードを入力"
            disabled={loading}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column' }}>
          設置場所で検索
          <input
            type="text"
            value={localSearch.location}
            onChange={(e) => handleChange('location', e.target.value)}
            style={{ padding: '4px', border: '1px inset #d0d0d0', fontSize: '11px' }}
            placeholder="例: 第一園舎"
            disabled={loading}
          />
        </label>
      </div>

      <div style={{ flex: 1, overflow: 'auto', border: '1px inset #d0d0d0' }}>
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center', fontSize: '11px' }}>読み込み中...</div>
        ) : assets.length === 0 ? (
          <div style={{ padding: '20px', fontSize: '11px', color: '#666' }}>
            条件に一致する固定資産はありません。
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ backgroundColor: '#e0e0e0' }}>
                <th style={{ border: '1px solid #999', padding: '4px', width: '90px' }}>資産コード</th>
                <th style={{ border: '1px solid #999', padding: '4px', width: '160px' }}>資産名称</th>
                <th style={{ border: '1px solid #999', padding: '4px', width: '90px' }}>取得日</th>
                <th style={{ border: '1px solid #999', padding: '4px', width: '100px', textAlign: 'right' }}>
                  取得価格
                </th>
                <th style={{ border: '1px solid #999', padding: '4px', width: '60px' }}>耐用年数</th>
                <th style={{ border: '1px solid #999', padding: '4px', width: '120px' }}>設置場所</th>
                <th style={{ border: '1px solid #999', padding: '4px', width: '70px' }}>状態</th>
                <th style={{ border: '1px solid #999', padding: '4px', width: '150px' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => {
                const isSelected = selectedAssetId === asset.id;
                return (
                  <tr
                    key={asset.id}
                    style={{
                      backgroundColor: isSelected ? '#cce7ff' : '#ffffff',
                      cursor: 'pointer',
                    }}
                    onClick={() => onSelect(asset)}
                  >
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>{asset.asset_code}</td>
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>{asset.asset_name}</td>
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                      {formatDate(asset.acquisition_date)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                      {formatCurrency(asset.acquisition_cost)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center' }}>
                      {asset.useful_life_years}年
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>{asset.location ?? ''}</td>
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>{asset.status}</td>
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(asset);
                          }}
                          style={{
                            backgroundColor: '#f0f0f0',
                            border: '2px outset #e0e0e0',
                            padding: '2px 6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`${asset.asset_name} を削除しますか？`)) {
                              onDelete(asset);
                            }
                          }}
                          style={{
                            backgroundColor: '#ffecec',
                            border: '2px outset #ffb4b4',
                            padding: '2px 6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          削除
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCalculate(asset);
                          }}
                          style={{
                            backgroundColor: '#e7f4ff',
                            border: '2px outset #c2e0ff',
                            padding: '2px 6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          減価償却
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '11px',
          marginTop: '4px',
        }}
      >
        <div>
          全{total}件 / {page}ページ目
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1 || loading}
            style={{
              backgroundColor: '#f0f0f0',
              border: '2px outset #e0e0e0',
              padding: '2px 8px',
              fontSize: '11px',
              cursor: page <= 1 || loading ? 'not-allowed' : 'pointer',
            }}
          >
            前へ
          </button>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(maxPage, page + 1))}
            disabled={page >= maxPage || loading}
            style={{
              backgroundColor: '#f0f0f0',
              border: '2px outset #e0e0e0',
              padding: '2px 8px',
              fontSize: '11px',
              cursor: page >= maxPage || loading ? 'not-allowed' : 'pointer',
            }}
          >
            次へ
          </button>
        </div>
      </div>
    </div>
  );
};

export default FixedAssetList;
